import { useEffect, useRef } from "react";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

import "./DilemaChart.css";
import wt from "../../images/white-lock.svg";

Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler);

/* ─── helpers ─────────────────────────────────────────── */
function polarToXY(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arcPath(cx, cy, r, startDeg, endDeg) {
  const [x1, y1] = polarToXY(cx, cy, r, startDeg);
  const [x2, y2] = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

const GAP = 0;    // degrees gap between arc segments
const SIZE = 200; // SVG / container size in px

/* ─── DonutRing with Percentage Labels ───────────────────────────────────────── */
function DonutRing({ colors, values, labels, total }) {
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const outerR = SIZE / 2 - 6;
  const thickness = 35;
  const r = outerR - thickness / 2;

  // Calculate percentages
  // const total = values.reduce((sum, val) => sum + val, 0);
  const percentages = values.map(val => 
    total > 0 ? Math.round((val / total) * 100) : 0
  );

  // Position for percentage labels (middle of the ring thickness)
  const labelRadius = outerR - thickness / 2;

  return (
    <svg
      className="vmtg-donut"
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
    >
      {/* White background circle */}
      <circle cx={cx} cy={cy} r={outerR + 2} fill="white" />
      
      {/* Donut segments */}
      {colors.map((color, i) => {
        const start = i * 90 + GAP / 2;
        const end = (i + 1) * 90 - GAP / 2;
        return (
          <path
            key={i}
            d={arcPath(cx, cy, r, start, end)}
            stroke={color}
            strokeWidth={thickness}
            fill="none"
            strokeLinecap="butt"
          />
        );
      })}
      
      {/* Inner white circle */}
      <circle cx={cx} cy={cy} r={outerR - thickness} fill="white" />

      {/* Percentage labels - positioned on the colored segments */}
      {percentages.map((percent, i) => {
        const midAngle = i * 90 + 45; // Middle of each segment
        const [x, y] = polarToXY(cx, cy, labelRadius, midAngle);
        
        return (
          <g key={`label-${i}`}>
            {/* Percentage text */}
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="white"
              fontSize="16"
              fontWeight="700"
              className="vmtg-percentage-label"
              style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
            >
              {percent}%
            </text>
          </g>
        );
      })}

      {/* Letter labels outside the ring */}
      {labels.map((label, i) => {
        const midAngle = i * 90 + 45;
        const letterRadius = outerR + 15;
        const [x, y] = polarToXY(cx, cy, letterRadius, midAngle);
        
        return (
          <text
            key={`letter-${i}`}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#666"
            fontSize="0"
            fontWeight="600"
            className="vmtg-letter-label"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

export default function VMTGChart({ DilemmaTypes, UserResult }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const scores = UserResult?.scores ? JSON.parse(UserResult.scores) : {};

  const total = Object.values(scores).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0
  );

  const values = DilemmaTypes.map(item => scores[item.letter] ?? 0);
  const labels = DilemmaTypes.map(item => item.letter);
  const colors = DilemmaTypes.map(item => item.letter_color);

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.data.datasets[0].data = values;
      chartRef.current.update();
      return;
    }

    chartRef.current = new Chart(canvasRef.current.getContext("2d"), {
      type: "radar",
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: "#ba90c8",
            borderColor: "rgba(150,130,195,0.75)",
            borderWidth: 1.5,
            pointBackgroundColor: colors,
            pointBorderColor: "#fff",
            pointRadius: 3.5,
            pointBorderWidth: 1.5,
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: {
          r: {
            min: 0,
            max: 10,
            ticks: { display: false, stepSize: 1 },
            grid: { color: "rgba(94, 94, 94, 0.28)", circular: false },
            angleLines: { color: "#91619a", lineWidth: 1 },
            pointLabels: { display: false },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [values]);

  return (
    <div className="vmtg-wrapper">
      <div className="vmtg-ring-wrap unlock-not">
        <DonutRing colors={colors} values={values} total={total} labels={labels} />
        <div className="vmtg-radar-wrap">
          <canvas ref={canvasRef} width={120} height={120} />
          <span>
            <img src={wt} alt="" />
          </span>
        </div>
      </div>
    </div>
  );
}